import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { getAvailability } from '@/services/bookingService';
import { trackEvent } from '@/lib/analytics';
import { scrollToId } from '@/lib/scroll';
import { cn } from '@/lib/utils';

const pad = (n) => String(n).padStart(2, '0');

export const dublinDayLabel = (dayKey) =>
  new Intl.DateTimeFormat('en-IE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'Europe/Dublin',
  }).format(new Date(`${dayKey}T12:00:00Z`));

// Step 2 — monthly calendar of genuinely available dates (server-calculated)
// with times listed per selected day. Europe/Dublin display; UTC underneath.
export const DateTimeStep = ({ meetingType, slot, onSelectSlot, onBack, onNext }) => {
  const now = new Date();
  const initialMonth = `${now.getFullYear()}-${pad(now.getMonth() + 1)}`;
  const [month, setMonth] = useState(initialMonth);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedDay, setSelectedDay] = useState(slot?.dayKey || null);

  const load = useCallback(async (m) => {
    setLoading(true);
    setError(false);
    try {
      setData(await getAvailability(meetingType.meetingTypeId, m));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [meetingType.meetingTypeId]);

  useEffect(() => {
    load(month);
  }, [month, load]);

  const cells = useMemo(() => {
    if (!data) return [];
    const blanks = Array.from({ length: data.firstWeekday }, () => null);
    const days = Array.from({ length: data.daysInMonth }, (_, i) => `${month}-${pad(i + 1)}`);
    return [...blanks, ...days];
  }, [data, month]);

  const canPrev = data && month > data.minMonth;
  const canNext = data && month < data.maxMonth;
  const daySlots = (selectedDay && data?.days?.[selectedDay]) || [];

  // Live state: no opened availability at all → honest message + enquiry CTA.
  if (!loading && !error && data?.liveStatus === 'updating') {
    return (
      <div data-testid="booking-updating" className="rounded-xl border border-slate-200 bg-mist p-8 text-center sm:p-12">
        <p className="text-lg font-extrabold text-navy-900">No online times are open for this meeting yet</p>
        <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-slate-600">
          Send us an enquiry and we’ll arrange a suitable time — or go back and choose a different
          meeting type.
        </p>
        <button
          type="button"
          onClick={() => scrollToId('#contact-enquiry')}
          data-testid="booking-updating-enquiry-btn"
          className="mt-6 inline-flex items-center gap-2 rounded-md bg-navy-900 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-navy-700"
        >
          Send an Enquiry
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </button>
        <div className="mt-8 text-left">
          <button type="button" onClick={onBack} data-testid="booking-back-datetime" className="inline-flex items-center gap-2 text-sm font-bold text-navy-900 underline-offset-2 hover:underline">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="booking-step-datetime">
      <h3 className="text-xl font-extrabold tracking-tight text-navy-900">Choose a date and time</h3>
      <p className="mt-2 text-sm font-medium text-slate-500">
        All times are shown in Irish time (Europe/Dublin).
      </p>

      <div className="mt-6 grid gap-8 lg:grid-cols-[1fr_320px]">
        <div className="rounded-xl border border-slate-200 bg-white p-5 sm:p-6">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => canPrev && setMonth((m) => `${+m.slice(0, 4) - (m.slice(5) === '01' ? 1 : 0)}-${m.slice(5) === '01' ? '12' : pad(+m.slice(5) - 1)}`)}
              disabled={!canPrev}
              aria-label="Previous month"
              data-testid="cal-prev-month"
              className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 text-navy-900 transition-colors hover:bg-navy-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            </button>
            <p className="text-base font-extrabold text-navy-900" data-testid="cal-month-label" aria-live="polite">
              {loading && !data ? 'Loading…' : data?.monthLabel}
            </p>
            <button
              type="button"
              onClick={() => canNext && setMonth((m) => `${m.slice(5) === '12' ? +m.slice(0, 4) + 1 : +m.slice(0, 4)}-${m.slice(5) === '12' ? '01' : pad(+m.slice(5) + 1)}`)}
              disabled={!canNext}
              aria-label="Next month"
              data-testid="cal-next-month"
              className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 text-navy-900 transition-colors hover:bg-navy-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16" data-testid="cal-loading" role="status">
              <Loader2 className="h-6 w-6 animate-spin text-navy-900" aria-hidden="true" />
              <span className="sr-only">Loading available dates</span>
            </div>
          ) : error ? (
            <div className="py-14 text-center" data-testid="cal-error" role="alert">
              <p className="text-sm font-bold text-navy-900">Available times could not be loaded.</p>
              <button type="button" onClick={() => load(month)} data-testid="cal-retry" className="mt-3 text-sm font-bold text-gold-700 underline-offset-2 hover:underline">
                Try again
              </button>
            </div>
          ) : (
            <div className="mt-5 grid grid-cols-7 gap-1" role="grid" aria-label={`Available dates for ${data?.monthLabel}`}>
              {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((d) => (
                <div key={d} className="py-1 text-center text-[11px] font-bold uppercase tracking-wide text-slate-400" role="columnheader">
                  {d}
                </div>
              ))}
              {cells.map((dayKey, i) => {
                if (!dayKey) return <div key={`blank-${i}`} aria-hidden="true" />;
                const count = data?.days?.[dayKey]?.length || 0;
                const available = count > 0;
                const selected = selectedDay === dayKey;
                const dayNum = parseInt(dayKey.slice(-2), 10);
                return (
                  <button
                    key={dayKey}
                    type="button"
                    role="gridcell"
                    disabled={!available}
                    aria-pressed={selected}
                    aria-label={
                      available
                        ? `${dublinDayLabel(dayKey)}, ${count} time${count === 1 ? '' : 's'} available`
                        : `${dublinDayLabel(dayKey)}, not available`
                    }
                    onClick={() => {
                      setSelectedDay(dayKey);
                      onSelectSlot(null);
                      trackEvent('date_selected');
                    }}
                    data-testid={`cal-day-${dayKey}`}
                    className={cn(
                      'flex h-11 flex-col items-center justify-center rounded-md text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900',
                      available && 'font-bold text-navy-900 hover:bg-navy-50',
                      !available && 'cursor-not-allowed text-slate-300',
                      selected && 'bg-navy-900 text-white hover:bg-navy-900'
                    )}
                  >
                    {dayNum}
                    {available && (
                      <span className={cn('mt-0.5 h-1 w-1 rounded-full', selected ? 'bg-gold-500' : 'bg-gold-600')} aria-hidden="true" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-5 sm:p-6" aria-live="polite">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Available times</p>
          {!selectedDay ? (
            <p className="mt-4 text-sm leading-relaxed text-slate-500" data-testid="times-placeholder">
              Select an available date to see times.
            </p>
          ) : daySlots.length === 0 ? (
            <p className="mt-4 text-sm leading-relaxed text-slate-500" data-testid="times-empty">
              No times remain on {dublinDayLabel(selectedDay)}. Please choose another date.
            </p>
          ) : (
            <>
              <p className="mt-2 text-sm font-bold text-navy-900" data-testid="times-day-label">
                {dublinDayLabel(selectedDay)}
              </p>
              <div className="mt-4 grid grid-cols-2 gap-2" role="radiogroup" aria-label="Available times">
                {daySlots.map((s) => {
                  const active = slot?.startUtc === s.startUtc;
                  return (
                    <button
                      key={s.startUtc}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => onSelectSlot({ ...s, dayKey: selectedDay })}
                      data-testid={`time-${s.time.replace(':', '')}`}
                      className={cn(
                        'rounded-md border-2 px-3 py-2.5 text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900',
                        active
                          ? 'border-navy-900 bg-navy-900 text-white'
                          : 'border-slate-200 bg-white text-navy-900 hover:border-navy-300 hover:bg-navy-50'
                      )}
                    >
                      {s.time}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="mt-8 flex items-center justify-between">
        <button type="button" onClick={onBack} data-testid="booking-back-datetime" className="inline-flex items-center gap-2 text-sm font-bold text-navy-900 underline-offset-2 hover:underline">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={!slot}
          data-testid="booking-next-datetime"
          className="group inline-flex items-center gap-2 rounded-md bg-navy-900 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-navy-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Enter Your Details
          <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};
